import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { map } from 'rxjs/operators';
import { Observable, of as observableOf, merge } from 'rxjs';
import { UserEx } from 'src/app/services/user.service';


/**
 * Data source for the UserManage view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class UserManageDataSource extends DataSource<UserEx> {
  data: UserEx[];

  constructor(private paginator: MatPaginator, private sort: MatSort, data: UserEx[]) {
    super();
    this.data = data;
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<UserEx[]> {
    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations: Observable<unknown>[] = [
      observableOf(this.data),
      this.paginator.page,
      this.sort.sortChange
    ];

    // Set the paginators length
    this.paginator.length = this.data.length;

    return merge(...dataMutations).pipe(map(() => {
      return this.getPagedData(this.getSortedData([...this.data]));
    })) as Observable<UserEx[]>;
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() { }

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: UserEx[]) {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return data.splice(startIndex, this.paginator.pageSize);
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: UserEx[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'name': return compare(a.displayName, b.displayName, isAsc);
        case 'id': return compareId(a.id, b.id, isAsc);
        case 'tagId': return compare(a.key, b.key, isAsc);
        case 'auth': return compare(a.authority, b.authority, isAsc);
        case 'manageAuth': return compare(a.manageAuthority, b.manageAuthority, isAsc);
        case 'team': return compare(a.teamName, b.teamName, isAsc);
        default: return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a, b, isAsc) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

// id="aaa1234"といった接頭辞(aaaなど)に対応 (英 + 数パターンのみ想定)
function compareId(a: string, b: string, isAsc) {
  // 文字と数字に分割する。
  const regex = /([\D]+)|([\d]+)/gi;
  const aSplit: string[] = a.match(regex);
  const bSplit: string[] = b.match(regex);

  // ["数値","文字"]に辞書順で並び替え
  aSplit.sort();
  bSplit.sort();

  // 比較
  if (aSplit.length === 2 && bSplit.length === 2) {
    if (aSplit[1] === bSplit[1]) {
      // 接頭辞が同じ場合
      // 数値の比較で良い
      return (+aSplit[0] < +bSplit[0] ? -1 : 1) * (isAsc ? 1 : -1);
    } else {
      // 接頭辞が異なる場合
      // 接頭辞の辞書順で比較
      return (aSplit[1] < bSplit[1] ? -1 : 1) * (isAsc ? 1 : -1);
    }
  } else if (aSplit.length === 1 && bSplit.length === 1) {
    // どちらも接頭辞がない場合
    // 数値の比較で良い
    return (+aSplit[0] < +bSplit[0] ? -1 : 1) * (isAsc ? 1 : -1);
  } else if (aSplit.length === 2) {
    // 接頭辞がある方が大きい
    return (1) * (isAsc ? 1 : -1);
  } else if (bSplit.length === 2) {
    // 接頭辞がある方が大きい
    return (-1) * (isAsc ? 1 : -1);
  }

}
